
import React, { useState, useEffect, useMemo } from "react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameDay, isSameMonth, parseISO, startOfDay } from "date-fns";
import { CalendarDay } from "./CalendarDay";
import { calculateWeekHeight } from "./utils/calendarUtils";

interface MonthCalendarProps {
  month: Date;
  events: any[];
  onEventClick?: (event: any) => void;
  onDayClick?: (date: Date) => void;
  variations?: any[];
}

export const MonthCalendar: React.FC<MonthCalendarProps> = ({ 
  month, 
  events = [], 
  onEventClick,
  onDayClick,
  variations = []
}) => {
  const [calendarDays, setCalendarDays] = useState<Date[][]>([]);
  const gridStart = useMemo(() => startOfWeek(startOfMonth(month)), [month]);
  
  // Generate calendar days
  useEffect(() => {
    const dateArray: Date[][] = [];
    const startDate = startOfWeek(startOfMonth(month));
    const endDate = endOfWeek(endOfMonth(month));
    
    let currentDate = startDate;
    let weekDays: Date[] = [];
    
    while (currentDate <= endDate) {
      if (weekDays.length === 7) {
        dateArray.push(weekDays);
        weekDays = [];
      }
      weekDays.push(new Date(currentDate));
      currentDate = addDays(currentDate, 1);
    }
    
    // Add the last week if it has days
    if (weekDays.length > 0) {
      dateArray.push(weekDays);
    }
    
    setCalendarDays(dateArray);
  }, [month]);
  
  // Filter events for each day
  const getEventsForDay = (day: Date) => {
    const dayStart = startOfDay(day);
    return events.filter(event => {
      // Handle different date formats and ensure we're working with Date objects
      const startDate = event.startDate instanceof Date ? event.startDate : parseISO(event.startDate as unknown as string);
      const endDate = event.endDate instanceof Date ? event.endDate : parseISO(event.endDate as unknown as string);
      const eventStart = startOfDay(startDate);
      const eventEnd = startOfDay(endDate);
      
      // Check if the day is between the start and end dates (inclusive)
      return (
        eventStart <= dayStart && dayStart <= eventEnd
      );
    });
  };
  
  // Calculate max events per day in each week
  const weekHeights = useMemo(() => {
    return calendarDays.map(week => {
      // Find the maximum number of events in any day of this week
      const maxEventsInWeek = Math.max(
        ...week.map(day => getEventsForDay(day).length),
        0
      );
      
      // Calculate the height based on the maximum events
      return calculateWeekHeight(maxEventsInWeek);
    });
  }, [calendarDays, events]);
  
  // Month header
  const monthYearHeader = useMemo(() => {
    return format(month, "MMMM yyyy");
  }, [month]);
  
  // Weekday headers
  const weekdays = useMemo(() => {
    const weekStartDate = startOfWeek(new Date());
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const day = addDays(weekStartDate, i);
      days.push(format(day, "EEE"));
    }
    
    return days;
  }, []);

  // Get today's date for comparison
  const today = useMemo(() => new Date(), []);
  
  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      <div className="bg-gray-50 p-3 border-b">
        <h3 className="font-semibold text-gray-700">{monthYearHeader}</h3>
      </div>
      
      <div className="grid grid-cols-7 bg-gray-100">
        {weekdays.map((day, index) => (
          <div 
            key={`header-${index}`}
            className="text-center text-xs py-2 font-medium text-gray-500"
          >
            {day}
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-1 divide-y">
        {calendarDays.map((week, weekIndex) => (
          <div 
            key={`week-${weekIndex}`} 
            className="grid grid-cols-7 divide-x"
            style={{ height: `${weekHeights[weekIndex]}px` }}
          >
            {week.map((day, dayIndex) => {
              const dayEvents = getEventsForDay(day);
              const isCurrentMonth = isSameMonth(day, month);
              const isToday = isSameDay(day, today);
              
              return (
                <CalendarDay
                  key={`day-${weekIndex}-${dayIndex}`}
                  date={day}
                  events={dayEvents}
                  isCurrentMonth={isCurrentMonth}
                  isToday={isToday}
                  onEventClick={onEventClick}
                  onAddSession={onDayClick}
                  maxVisibleEvents={6}
                  variations={variations}
                  monthGridStart={gridStart}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
